import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Brain, CircleNotch, ArrowSquareOut, Lightning, Buildings, Sparkle } from "@phosphor-icons/react";

const GAP_LABEL = { domaine_web: "Site web à confirmer", decideurs: "Décideurs à identifier", email_verifie: "Email à vérifier", tjm: "TJM inconnu" };

const Chips = ({ items, testid, tone = "accent" }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((t) => (
      <span key={t} className={`text-[11px] px-2 py-0.5 rounded border ${tone === "accent" ? "bg-accent/10 text-accent border-accent/30" : "bg-surface-2 text-fg/80 border-border"}`}>{t}</span>
    ))}
  </div>
);

const Block = ({ title, items }) => (items?.length ? (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-muted mb-1">{title}</div>
    <ul className="space-y-1">{items.map((x) => <li key={x} className="text-xs text-fg/90 leading-relaxed">• {x}</li>)}</ul>
  </div>
) : null);

export const IntelDetails = ({ intel, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {intel.summary && <p className="text-sm text-fg/90 leading-relaxed">{intel.summary}</p>}
    <div className="grid md:grid-cols-2 gap-4">
      <Block title="Projets IT" items={intel.it_projects} />
      <Block title="Profils recherchés" items={intel.hiring_focus} />
      <Block title="Recours à la prestation" items={intel.outsourcing_signals} />
      {intel.technologies?.length > 0 && <div><div className="text-[11px] uppercase tracking-wider text-muted mb-1">Technologies</div><Chips items={intel.technologies} tone="neutral" /></div>}
    </div>
    {intel.sources?.length > 0 && (
      <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1 border-t border-border/60">
        <span className="text-[11px] text-muted">Sources :</span>
        {intel.sources.slice(0, 5).map((s) => (
          <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="text-[11px] text-accent hover:underline flex items-center gap-1 max-w-[260px] truncate">
            <ArrowSquareOut size={10} /> {s.title || s.url}
          </a>
        ))}
      </div>
    )}
  </div>
);

function TargetCard({ t, intel, intent, onIntel }) {
  const [busy, setBusy] = useState(false);
  const deepen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/research/companies/${t.company_id}/intel`, { skills: intent?.core_skills || intent?.skills, role: intent?.role });
      onIntel(t.company_id, data.research);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-lg p-4 bg-surface-2/40 transition-colors duration-300 hover:border-accent/30" data-testid={`intel-target-${t.company_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <Link to={`/app/company/${t.company_id}`} className="font-display font-semibold text-fg hover:text-accent flex items-center gap-2"><Buildings size={15} />{t.name}</Link>
          <div className="text-[11px] text-muted mt-0.5">{(t.why || []).join(" · ")}</div>
        </div>
        <span className="text-xs font-mono font-bold text-accent border border-accent/30 rounded-full px-2 py-0.5 shrink-0" title="Priorité de recherche">{t.priority}</span>
      </div>
      {(t.gaps || []).length > 0 && <div className="mb-3"><Chips items={t.gaps.map((g) => GAP_LABEL[g] || g)} tone="neutral" testid={`intel-gaps-${t.company_id}`} /></div>}
      {intel ? <IntelDetails intel={intel} testid={`intel-research-${t.company_id}`} /> : (
        <button onClick={deepen} disabled={busy} data-testid={`intel-deepen-${t.company_id}`}
          className="text-xs flex items-center gap-1.5 border border-accent/40 text-accent rounded-md px-3 py-1.5 hover:bg-accent/10 transition-colors disabled:opacity-60">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />} {busy ? "Recherche en cours…" : "Approfondir avec la recherche IA"}
        </button>
      )}
    </div>
  );
}

export const OpportunityIntel = ({ missionId, context, initialStatus }) => {
  const [research, setResearch] = useState(context?.research || {});
  const [status, setStatus] = useState(initialStatus || "skipped");
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!missionId || !["pending", "running"].includes(status)) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/research/missions/${missionId}`);
        if (stop) return;
        setResearch(data.result?.context?.research || {});
        setStatus(data.research_status || "done");
      } catch { /* mission expirée */ }
    };
    const t = setInterval(tick, 2500);
    tick();
    return () => { stop = true; clearInterval(t); };
  }, [missionId, status]);
  const targets = context?.research_targets || [];
  if (!targets.length) return null;
  const running = ["pending", "running"].includes(status);
  const sorted = [...targets].sort((a, b) => (research[b.company_id] ? 1 : 0) - (research[a.company_id] ? 1 : 0));
  return (
    <section className="surface rounded-lg p-5 mb-6" data-testid="opportunity-intel">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="overline flex items-center gap-2"><Brain size={14} className="text-accent" weight="fill" /> Opportunity Intelligence</div>
        <button onClick={() => setOpen(!open)} className="text-xs text-muted hover:text-fg" data-testid="opportunity-intel-toggle">{open ? "Réduire" : "Afficher"}</button>
      </div>
      <p className="text-xs text-muted mb-4 flex items-center gap-1.5" data-testid="opportunity-intel-status">
        {running ? <><CircleNotch size={12} className="animate-spin text-accent" /> Recherche approfondie en cours sur les entreprises prioritaires…</>
          : <><Sparkle size={12} className="text-accent" /> {Object.keys(research).length} entreprise(s) analysée(s) à partir de sources web publiques — vérifiez toujours avant de contacter.</>}
      </p>
      {open && <div className="grid xl:grid-cols-2 gap-3">{sorted.map((t) => (
        <TargetCard key={t.company_id} t={t} intel={research[t.company_id]} intent={context.intent}
          onIntel={(cid, r) => setResearch((prev) => ({ ...prev, [cid]: r }))} />
      ))}</div>}
    </section>
  );
};
